import * as React from "react";
import { Trash2, TriangleAlert } from "lucide-react";

import type { Theme } from "@/data/config";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/** Destructive confirm for deleting a theme: the partner must type the theme's
 *  exact name before the delete button enables. Open when `theme` is non-null. */
export function DeleteThemeDialog({
  theme,
  onOpenChange,
  onConfirm,
}: {
  theme: Theme | null;
  onOpenChange: (open: boolean) => void;
  onConfirm: (theme: Theme) => void;
}) {
  const [value, setValue] = React.useState("");

  // Clear the typed name whenever the target theme changes (or the dialog reopens).
  React.useEffect(() => {
    setValue("");
  }, [theme?.id]);

  const confirmed = !!theme && value.trim() === theme.name;

  return (
    <Dialog open={theme !== null} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl border-border-strong bg-popover shadow-portal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TriangleAlert className="size-[18px] text-[#ff7a7a]" />
            Delete theme
          </DialogTitle>
          <DialogDescription>
            This permanently deletes{" "}
            <span className="font-semibold text-foreground">{theme?.name}</span> and its colors.
            This can’t be undone.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-[7px]">
          <Label htmlFor="confirm-theme-name">
            Type <span className="font-semibold text-foreground">{theme?.name}</span> to confirm
          </Label>
          <Input
            id="confirm-theme-name"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            autoComplete="off"
            autoFocus
            placeholder={theme?.name}
            onKeyDown={(e) => {
              if (e.key === "Enter" && confirmed && theme) onConfirm(theme);
            }}
          />
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost" size="sm">
              Cancel
            </Button>
          </DialogClose>
          <Button
            variant="danger"
            size="sm"
            disabled={!confirmed}
            onClick={() => {
              if (theme && confirmed) onConfirm(theme);
            }}
          >
            <Trash2 className="size-4" /> Delete theme
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
